import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import {
  getActiveIntent,
  getPrimaryIntent,
  getSecondaryIntents,
  isKnownIntent,
  isMultiIntentSession,
} from './intentMapping';

// The optional intent fields of the events Cashier sends to the merchant page.
//
// Only the transaction- and tab-specific events of the Panoramic 2.0 project
// carry them. The value is the active tab's intent at the moment the event is
// produced, so an event queued before the merchant domain is known keeps the
// intent it was produced with. The initialisation message, the domain
// verification reply and the redirect-window readiness message never carry it.

export const INTENT_EVENT_TYPES = [
  'payment_method_selected',
  'payment_methods_loaded',
  'client_click',
  'validation_success',
  'validation_failed',
  'cashier_submit',
  'transaction_attempted',
  'transaction',
  'transaction_failed',
  'redirection',
  'hosted_payment_fields_loaded',
  'hpf_form_valid',
  'hpf_form_invalid',
  'apm_fields_loaded',
  'apm_form_valid',
  'apm_form_invalid',
  'thank_you_page_loaded',
  'cashier_loaded',
];

/**
 * `{ intent }` for the active tab, or nothing when the session's intent is not
 * known yet or Cashier is not running the Panoramic 2.0 project.
 */
export const getEventIntentFields = (
  state: any,
  panoramic: boolean = isPanoramicTheme,
): { intent?: string } => {
  if (!panoramic) return {};

  const intent = getActiveIntent(state);

  return isKnownIntent(intent) ? { intent: intent as string } : {};
};

/**
 * The intent fields of the session-level events (the loaded event and the
 * loading-failed event): the intent plus, for a multi-intent session only, the
 * session's extra intents. Nothing when the session's intent configuration was
 * not resolved yet.
 */
export const getSessionEventIntentFields = (
  state: any,
  panoramic: boolean = isPanoramicTheme,
): { intent?: string; secondary_intents?: string[] } => {
  if (!panoramic || !isKnownIntent(getPrimaryIntent(state))) return {};

  return {
    ...getEventIntentFields(state, panoramic),
    ...(isMultiIntentSession(state) && { secondary_intents: getSecondaryIntents(state) }),
  };
};

/**
 * Adds the active tab's intent to an event on the list, unless the producer
 * already set one. Every other event is returned untouched.
 */
export const withEventIntent = <T extends { event_type?: string; intent?: string }>(
  event: T,
  state: any,
  panoramic: boolean = isPanoramicTheme,
): T => {
  if (!event || INTENT_EVENT_TYPES.indexOf(event.event_type as string) === -1) return event;
  if (event.intent !== undefined) return event;

  return { ...event, ...getEventIntentFields(state, panoramic) };
};
